import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sistemaService } from '../services/sistemaService';
import { ApiError, WebhookLog, Estado, Cidade } from '../types/sistema';
import {
  Settings,
  Users,
  AlertTriangle,
  Radio,
  MapPin,
  Search,
  ShieldCheck,
  UserPlus,
  RefreshCw,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  KeyRound,
  Building2,
  Sliders,
  Receipt,
  MessageCircle
} from 'lucide-react';
import { ModuloPermissoes } from '../components/ModuloPermissoes';
import { GerenciadorModelos } from '../components/editorDocumentos/GerenciadorModelos';
import { EditorDocumentoPage } from '../components/editorDocumentos/EditorDocumentoPage';
import { DocumentoModelo } from '../types/documentoModelo';
import {
  UserPermissionProfile,
  GroupWithPrograms,
  SystemProgram,
  userManagementService
} from '../services/userManagementService';
import { EditarPermissoesModal } from '../components/EditarPermissoesModal';

// Componentes modulares e isolados de Gestão de Usuários
import { EditarUsuarioModal } from '../components/usuarios/EditarUsuarioModal';
import { NovoUsuarioModal } from '../components/usuarios/NovoUsuarioModal';
import { AlterarMinhaSenhaModal } from '../components/usuarios/AlterarMinhaSenhaModal';
import { ConfirmDeleteUserModal } from '../components/usuarios/ConfirmDeleteUserModal';
import { UsuariosStatsCards } from '../components/usuarios/UsuariosStatsCards';
import { UsuariosTable } from '../components/usuarios/UsuariosTable';
import { EstadosCidadesSection } from '../components/sistema/EstadosCidadesSection';
import { ConfiguracaoEmpresaSection } from '../components/sistema/ConfiguracaoEmpresaSection';
import { ConfiguracaoSaldoPropostas } from '../components/sistema/ConfiguracaoSaldoPropostas';
import { ConfiguracaoFiscalSection } from '../components/sistema/ConfiguracaoFiscalSection';
import { NotificacoesSection } from '../components/sistema/NotificacoesSection';

export function SistemaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [tab, setTab] = useState<'empresa' | 'fiscal' | 'notificacoes' | 'saldo' | 'usuarios' | 'permissoes' | 'modelos' | 'api' | 'webhooks' | 'locais'>(
    (urlTab === 'fiscal' || urlTab === 'empresa' || urlTab === 'notificacoes' || urlTab === 'saldo' || urlTab === 'usuarios' || urlTab === 'permissoes' || urlTab === 'modelos' || urlTab === 'api' || urlTab === 'webhooks' || urlTab === 'locais') ? urlTab : 'empresa'
  );

  const handleTabChange = (newTab: typeof tab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };
  
  // Dados de Usuários e Permissões
  const [usuarios, setUsuarios] = useState<UserPermissionProfile[]>([]);
  const [availableGroups, setAvailableGroups] = useState<GroupWithPrograms[]>([]);
  const [allPrograms, setAllPrograms] = useState<SystemProgram[]>([]);
  
  // Outras tabelas do sistema
  const [apiErrors, setApiErrors] = useState<ApiError[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookLog[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [loading, setLoading] = useState(true);

  // Estados de Filtros e Busca
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const [roleFilter, setRoleFilter] = useState<string>('todos');

  // Estados de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modais de Gestão de Usuários
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserPermissionProfile | null>(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  
  // Modal de Permissões RBAC
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<UserPermissionProfile | null>(null);
  const [isEditPermissionsModalOpen, setIsEditPermissionsModalOpen] = useState(false);

  // Modal de Alteração de Senha do próprio usuário
  const [isMyPasswordModalOpen, setIsMyPasswordModalOpen] = useState(false);

  // Confirmação de exclusão
  const [userToDelete, setUserToDelete] = useState<UserPermissionProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Modelo ativo no Editor Visual
  const [modeloEditor, setModeloEditor] = useState<DocumentoModelo | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Reseta a página para 1 quando alterar termo de busca ou filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, itemsPerPage]);

  async function fetchData() {
    try {
      setLoading(true);
      const [uList, gList, pList, errs, w, est, cid] = await Promise.all([
        userManagementService.getUsuariosComPermissoes(),
        userManagementService.getSystemGroups(),
        userManagementService.getSystemPrograms(),
        sistemaService.getApiErrors(),
        sistemaService.getWebhookLogs(),
        sistemaService.getEstados(),
        sistemaService.getCidades(),
      ]);

      setUsuarios(uList);
      setAvailableGroups(gList);
      setAllPrograms(pList);
      setApiErrors(errs);
      setWebhooks(w);
      setEstados(est);
      setCidades(cid);
    } catch (err) {
      console.error('Error fetching system parameters:', err);
    } finally {
      setLoading(false);
    }
  }

  // Ações em Usuários
  const handleToggleUserStatus = async (user: UserPermissionProfile) => {
    const success = await userManagementService.toggleUserStatus(user.id, user.active);
    if (success) {
      await fetchData();
    }
  };

  const handleRestoreUser = async (user: UserPermissionProfile) => {
    try {
      const email = user.email && user.email.includes('@') ? user.email : undefined;
      const res = await userManagementService.restaurarUsuario(user.id, email);
      if (res.success) {
        await fetchData();
      } else {
        alert(res.message || 'Erro ao restaurar usuário.');
      }
    } catch (e) {
      console.error('Erro ao restaurar usuário:', e);
      alert('Erro inesperado ao restaurar usuário.');
    }
  };

  const handleOpenEditUser = (user: UserPermissionProfile) => {
    setSelectedUserForEdit(user);
    setIsEditUserModalOpen(true);
  };

  const handleOpenEditPermissions = (user: UserPermissionProfile) => {
    setSelectedUserForPermissions(user);
    setIsEditPermissionsModalOpen(true);
  };

  const handleExcluirUsuario = (user: UserPermissionProfile) => {
    setUserToDelete(user);
    setDeleteError(null);
  };

  const executeDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await userManagementService.excluirUsuario(userToDelete.id);
      if (res.success) {
        await fetchData();
        setUserToDelete(null);
      } else {
        console.error("============= FRONTEND LOG DE ERRO DE EXCLUSÃO =============");
        console.error("Motivo da falha:", res.message);
        console.error("==============================================================");
        setDeleteError(res.message || 'Erro ao excluir o usuário no banco de dados.');
      }
    } catch (err: any) {
      console.error("============= FRONTEND LOG DE EXCEÇÃO DE EXCLUSÃO =============");
      console.error("Erro interno Frontend:", err);
      console.error("==============================================================");
      setDeleteError(err.message || 'Falha ao processar a exclusão de usuário.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtragem de Usuários
  const usuariosFiltrados = usuarios.filter((u) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      u.name?.toLowerCase().includes(term) ||
      u.login?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.auth_user_id?.toLowerCase().includes(term) ||
      String(u.id).includes(term);

    const isUserActive = u.active === 'Y' || u.active === 'S';
    const matchesStatus =
      statusFilter === 'todos' ||
      (statusFilter === 'ativo' && isUserActive) ||
      (statusFilter === 'inativo' && !isUserActive);

    const matchesRole = roleFilter === 'todos' || u.role === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  // Métricas
  const totalUsuarios = usuarios.length;
  const totalAtivos = usuarios.filter((u) => u.active === 'Y' || u.active === 'S').length;
  const totalInativos = totalUsuarios - totalAtivos;
  const totalComAuth = usuarios.filter((u) => u.auth_user_id).length;

  // Cálculo da Paginação
  const totalItems = usuariosFiltrados.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const usuariosPaginados = usuariosFiltrados.slice(startIndex, endIndex);

  // Gerador de botões de páginas numéricas
  const renderPaginationPages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, safeCurrentPage - 1);
      let end = Math.min(totalPages - 1, safeCurrentPage + 1);

      if (safeCurrentPage <= 3) {
        end = 4;
      } else if (safeCurrentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages.map((page, idx) => {
      if (typeof page === 'string') {
        return (
          <span key={`ellipsis-${idx}`} className="px-2.5 py-1 text-slate-400 text-xs font-bold">
            ...
          </span>
        );
      }

      const isCurrent = page === safeCurrentPage;
      return (
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className={`min-w-[32px] h-8 px-2.5 rounded-lg text-xs font-bold transition flex items-center justify-center cursor-pointer ${
            isCurrent
              ? 'bg-red-600 text-white shadow-md shadow-red-600/20 font-black'
              : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
          }`}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="h-7 w-7 text-red-600" />
            Configurações & Gestão do Sistema
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Administração de usuários, permissões RBAC, credenciais e integrações.
          </p>
        </div>
      </div>

      {/* Navegação por Abas */}
      <div className="border-b border-slate-200 flex gap-4 overflow-x-auto">
        <button
          onClick={() => handleTabChange('empresa')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'empresa'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Building2 className="h-4 w-4" />
          Dados da Empresa
        </button>
        <button
          onClick={() => handleTabChange('fiscal')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'fiscal'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Receipt className="h-4 w-4" />
          Configurações Fiscais (CT-e / NFS-e)
        </button>
        <button
          onClick={() => handleTabChange('notificacoes')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'notificacoes'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <MessageCircle className="h-4 w-4" />
          Notificações
        </button>
        <button
          onClick={() => handleTabChange('saldo')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'saldo'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Sliders className="h-4 w-4" />
          Regras de Saldo (O.S / Propostas)
        </button>
        <button
          onClick={() => handleTabChange('usuarios')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'usuarios'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users className="h-4 w-4" />
          Gestão de Usuários ({usuarios.length})
        </button>
        <button
          onClick={() => handleTabChange('permissoes')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'permissoes'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <ShieldCheck className="h-4 w-4" />
          Controle de Permissões (RBAC)
        </button>
        <button
          onClick={() => handleTabChange('modelos')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'modelos'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <FileText className="h-4 w-4" />
          Modelos de Documentos
        </button>
        <button
          onClick={() => handleTabChange('api')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'api'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <AlertTriangle className="h-4 w-4" />
          Erros de API ({apiErrors.length})
        </button>
        <button
          onClick={() => handleTabChange('webhooks')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'webhooks'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Radio className="h-4 w-4" />
          Webhooks ({webhooks.length})
        </button>
        <button
          onClick={() => handleTabChange('locais')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            tab === 'locais'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <MapPin className="h-4 w-4" />
          Locais ({estados.length} UFs / {cidades.length} Cidades)
        </button>
      </div>

      {/* Conteúdo das Abas */}
      {tab === 'empresa' ? (
        <ConfiguracaoEmpresaSection />
      ) : tab === 'fiscal' ? (
        <ConfiguracaoFiscalSection />
      ) : tab === 'notificacoes' ? (
        <NotificacoesSection />
      ) : tab === 'saldo' ? (
        <ConfiguracaoSaldoPropostas />
      ) : tab === 'usuarios' ? (
        <div className="space-y-4">
          {/* Cards de Métricas e Acesso Rápido */}
          <UsuariosStatsCards
            totalUsuarios={totalUsuarios}
            totalAtivos={totalAtivos}
            totalInativos={totalInativos}
            totalComAuth={totalComAuth}
            onOpenMyPasswordModal={() => setIsMyPasswordModalOpen(true)}
          />

          {/* Painel de Filtros e Busca */}
          <div className="bg-white p-4 rounded-xl shadow-xs border border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[300px]">
              {/* Campo de Busca */}
              <div className="relative rounded-md shadow-xs max-w-xs w-full">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  className="block w-full rounded-lg border-slate-300 pl-9 pr-3 focus:border-red-500 focus:ring-red-500 text-xs py-2 border outline-none"
                  placeholder="Buscar por nome, login, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Filtro por Status */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-xs text-slate-700 bg-white font-medium outline-none focus:border-red-500"
              >
                <option value="todos">Todos os Status</option>
                <option value="ativo">Apenas Ativos</option>
                <option value="inativo">Apenas Inativos</option>
              </select>

              {/* Filtro por Perfil / Role */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-xs text-slate-700 bg-white font-medium outline-none focus:border-red-500"
              >
                <option value="todos">Todos os Perfis</option>
                <option value="admin">Administradores</option>
                <option value="manager">Gerentes & Operadores</option>
                <option value="user">Usuários Padrão</option>
                <option value="client">Clientes & Obras</option>
                <option value="driver">Motoristas & Agregados</option>
              </select>

              {/* Seletor de Itens Por Página */}
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <span>Exibir:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-700 bg-white font-bold outline-none"
                >
                  <option value={10}>10 por pág.</option>
                  <option value={25}>25 por pág.</option>
                  <option value={50}>50 por pág.</option>
                  <option value={100}>100 por pág.</option>
                </select>
              </div>
            </div>

            {/* Ações do Topo */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchData()}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition cursor-pointer"
                title="Atualizar lista de usuários"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Recarregar
              </button>
              <button
                onClick={() => setIsModalCreateOpen(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-md shadow-red-600/20 transition cursor-pointer"
              >
                <UserPlus className="w-4 h-4" />
                + Cadastrar Usuário
              </button>
            </div>
          </div>

          {/* Tabela de Usuários */}
          <UsuariosTable
            usuarios={usuariosPaginados}
            loading={loading}
            onEditUser={handleOpenEditUser}
            onEditPermissions={handleOpenEditPermissions}
            onToggleStatus={handleToggleUserStatus}
            onDeleteUser={handleExcluirUsuario}
            onRestoreUser={handleRestoreUser}
          />

          {/* Rodapé de Paginação Completo */}
          {totalItems > 0 && (
            <div className="p-4 bg-white rounded-xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600 font-medium">
              <div>
                Exibindo <span className="font-bold text-slate-900">{startIndex + 1}</span> a{' '}
                <span className="font-bold text-slate-900">{Math.min(endIndex, totalItems)}</span> de{' '}
                <span className="font-bold text-slate-900">{totalItems}</span> usuários
              </div>

              <div className="flex items-center gap-1">
                {/* Primeira Página */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={safeCurrentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 transition cursor-pointer"
                  title="Primeira página"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>

                {/* Página Anterior */}
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safeCurrentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 transition cursor-pointer"
                  title="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Números das Páginas */}
                <div className="flex items-center gap-1 px-1">
                  {renderPaginationPages()}
                </div>

                {/* Próxima Página */}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safeCurrentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 transition cursor-pointer"
                  title="Próxima página"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* Última Página */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safeCurrentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 transition cursor-pointer"
                  title="Última página"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : tab === 'permissoes' ? (
        <ModuloPermissoes />
      ) : tab === 'modelos' ? (
        modeloEditor ? (
          <EditorDocumentoPage
            modelo={modeloEditor}
            onClose={() => {
              setModeloEditor(null);
              fetchData();
            }}
          />
        ) : (
          <GerenciadorModelos
            onOpenEditor={(modelo) => setModeloEditor(modelo)}
          />
        )
      ) : tab === 'api' ? (
        <div className="bg-white shadow-xs rounded-xl overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 font-bold text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3 text-left">ID</th>
                  <th className="px-6 py-3 text-left">Classe / Método</th>
                  <th className="px-6 py-3 text-left">URL</th>
                  <th className="px-6 py-3 text-left">Mensagem de Erro</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200 text-xs">
                {apiErrors.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      Nenhum erro de API registrado.
                    </td>
                  </tr>
                ) : (
                  apiErrors.map((err) => (
                    <tr key={err.id}>
                      <td className="px-6 py-4 font-mono">{err.id}</td>
                      <td className="px-6 py-4 font-medium text-slate-900">{err.classe} / {err.metodo}</td>
                      <td className="px-6 py-4 text-slate-500 max-w-xs truncate">{err.url}</td>
                      <td className="px-6 py-4 text-red-600">{err.erro_message || (err as any).mensagem || 'Erro'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'webhooks' ? (
        <div className="bg-white shadow-xs rounded-xl overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 font-bold text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3 text-left">ID</th>
                  <th className="px-6 py-3 text-left">Data</th>
                  <th className="px-6 py-3 text-left">Gateway ID</th>
                  <th className="px-6 py-3 text-left">Payload</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200 text-xs">
                {webhooks.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      Nenhum log de webhook registrado.
                    </td>
                  </tr>
                ) : (
                  webhooks.map((w) => (
                    <tr key={w.id}>
                      <td className="px-6 py-4 font-mono">{w.id}</td>
                      <td className="px-6 py-4 text-slate-500">{w.created_at || '-'}</td>
                      <td className="px-6 py-4 font-medium text-slate-900">{w.gateway_pagamento_id}</td>
                      <td className="px-6 py-4 font-mono text-slate-500 max-w-xs truncate">{w.payload}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EstadosCidadesSection
          estados={estados}
          cidades={cidades}
          loading={loading}
          onRefresh={fetchData}
        />
      )}

      {/* Modal de Edição de Usuário (Nome, Email, Senha, Perfil, Status) */}
      <EditarUsuarioModal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        user={selectedUserForEdit}
        availableGroups={availableGroups}
        onUserUpdated={fetchData}
      />

      {/* Modal de Criação de Novo Usuário */}
      <NovoUsuarioModal
        isOpen={isModalCreateOpen}
        onClose={() => setIsModalCreateOpen(false)}
        availableGroups={availableGroups}
        onUserCreated={fetchData}
      />

      {/* Modal de Alteração de Minhas Credenciais */}
      <AlterarMinhaSenhaModal
        isOpen={isMyPasswordModalOpen}
        onClose={() => setIsMyPasswordModalOpen(false)}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDeleteUserModal
        isOpen={Boolean(userToDelete)}
        onClose={() => setUserToDelete(null)}
        user={userToDelete}
        onConfirm={executeDeleteUser}
        isDeleting={isDeleting}
        error={deleteError}
      />

      {/* Modal de Permissões RBAC */}
      <EditarPermissoesModal
        isOpen={isEditPermissionsModalOpen}
        onClose={() => setIsEditPermissionsModalOpen(false)}
        user={selectedUserForPermissions}
        availableGroups={availableGroups}
        allPrograms={allPrograms}
        onSaved={fetchData}
      />
    </div>
  );
}
